import { z } from "zod";
import { IMAGE_SRC_PATTERN, SAFE_HREF_PATTERN } from "./primitives";

/**
 * Blog post body: Tiptap JSON restricted to an allow-list. The Go API normalizes every saved body to
 * exactly these nodes (apps/api/internal/blog/body.go); the engine renders them as React elements and
 * re-checks every URL, so a body is never turned into an HTML string.
 *
 *   blocks:  paragraph, heading (level 2|3), bulletList, orderedList (start), listItem, blockquote,
 *            horizontalRule, image (src, alt)
 *   inline:  text (marks: bold, italic, link{href}), hardBreak
 */

export type PostMark = { type: "bold" } | { type: "italic" } | { type: "link"; attrs: { href: string } };

export type PostInline = { type: "text"; text: string; marks?: PostMark[] } | { type: "hardBreak" };

export type PostBlock =
  | { type: "paragraph"; content?: PostInline[] }
  | { type: "heading"; attrs: { level: 2 | 3 }; content?: PostInline[] }
  | { type: "bulletList"; content: PostListItem[] }
  | { type: "orderedList"; attrs?: { start?: number }; content: PostListItem[] }
  | { type: "blockquote"; content: PostBlock[] }
  | { type: "horizontalRule" }
  | { type: "image"; attrs: { src: string; alt: string } };

export interface PostListItem {
  type: "listItem";
  content: PostBlock[];
}

export interface PostDoc {
  type: "doc";
  content: PostBlock[];
}

export const EMPTY_POST_DOC: PostDoc = { type: "doc", content: [] };

/** Words in a body (reading time). */
export function postWordCount(doc: PostDoc): number {
  let words = 0;
  const walk = (nodes: ReadonlyArray<PostBlock | PostInline | PostListItem> | undefined) => {
    for (const n of nodes ?? []) {
      if (n.type === "text") words += n.text.split(/\s+/).filter(Boolean).length;
      else if ("content" in n) walk(n.content as ReadonlyArray<PostBlock | PostInline | PostListItem>);
    }
  };
  walk(doc.content);
  return words;
}

// ── Rich text inside a Website Definition (lesson topics) ──────────────────────
// The same allow-list as blog bodies, but validated by the definition schema instead of the API's
// normalizer: the editor stores normalizePostDoc() output, so Tiptap's extra attrs never reach it.

const MAX_TEXT = 4000;
const href = z.string().max(2048).regex(SAFE_HREF_PATTERN);
const postMarkSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("bold") }),
  z.strictObject({ type: z.literal("italic") }),
  z.strictObject({ type: z.literal("link"), attrs: z.strictObject({ href }) }),
]);
const postInlineSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("text"), text: z.string().min(1).max(MAX_TEXT), marks: z.array(postMarkSchema).max(3).optional() }),
  z.strictObject({ type: z.literal("hardBreak") }),
]);
const inlines = z.array(postInlineSchema).max(400);

export const postBlockSchema: z.ZodType<PostBlock> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("paragraph"), content: inlines.optional() }),
    z.strictObject({ type: z.literal("heading"), attrs: z.strictObject({ level: z.union([z.literal(2), z.literal(3)]) }), content: inlines.optional() }),
    z.strictObject({ type: z.literal("bulletList"), content: z.array(postListItemSchema).max(100) }),
    z.strictObject({ type: z.literal("orderedList"), attrs: z.strictObject({ start: z.number().int().min(1).max(999).optional() }).optional(), content: z.array(postListItemSchema).max(100) }),
    z.strictObject({ type: z.literal("blockquote"), content: z.array(postBlockSchema).max(50) }),
    z.strictObject({ type: z.literal("horizontalRule") }),
    z.strictObject({ type: z.literal("image"), attrs: z.strictObject({ src: z.string().max(2048).regex(IMAGE_SRC_PATTERN), alt: z.string().max(200) }) }),
  ]),
) as z.ZodType<PostBlock>;
const postListItemSchema: z.ZodType<PostListItem> = z.lazy(() => z.strictObject({ type: z.literal("listItem"), content: z.array(postBlockSchema).max(20) }));

export const postDocSchema = z.strictObject({ type: z.literal("doc"), content: z.array(postBlockSchema).max(300) });

type Json = { type?: unknown; attrs?: Record<string, unknown>; content?: unknown; marks?: unknown; text?: unknown };
const nodes = (v: unknown): Json[] => (Array.isArray(v) ? (v.filter((n) => n && typeof n === "object") as Json[]) : []);

function normInline(list: unknown): PostInline[] {
  const out: PostInline[] = [];
  for (const n of nodes(list)) {
    if (n.type === "hardBreak") out.push({ type: "hardBreak" });
    else if (n.type === "text" && typeof n.text === "string" && n.text) {
      const marks: PostMark[] = [];
      for (const m of nodes(n.marks)) {
        if (m.type === "bold" || m.type === "italic") marks.push({ type: m.type });
        else if (m.type === "link" && typeof m.attrs?.href === "string" && href.safeParse(m.attrs.href).success) marks.push({ type: "link", attrs: { href: m.attrs.href } });
      }
      out.push(marks.length ? { type: "text", text: n.text.slice(0, MAX_TEXT), marks: marks.slice(0, 3) } : { type: "text", text: n.text.slice(0, MAX_TEXT) });
    }
  }
  return out.slice(0, 400);
}

function normBlocks(list: unknown, max: number): PostBlock[] {
  const out: PostBlock[] = [];
  for (const n of nodes(list)) {
    const content = normInline(n.content);
    const items = () => nodes(n.content).filter((i) => i.type === "listItem").map((i) => ({ type: "listItem" as const, content: normBlocks(i.content, 20) })).slice(0, 100);
    switch (n.type) {
      case "paragraph":
        out.push(content.length ? { type: "paragraph", content } : { type: "paragraph" });
        break;
      case "heading":
        out.push({ type: "heading", attrs: { level: n.attrs?.level === 3 ? 3 : 2 }, ...(content.length ? { content } : {}) });
        break;
      case "bulletList":
        out.push({ type: "bulletList", content: items() });
        break;
      case "orderedList": {
        const start = n.attrs?.start;
        out.push({ type: "orderedList", ...(typeof start === "number" && Number.isInteger(start) && start > 1 && start <= 999 ? { attrs: { start } } : {}), content: items() });
        break;
      }
      case "blockquote":
        out.push({ type: "blockquote", content: normBlocks(n.content, 50) });
        break;
      case "horizontalRule":
        out.push({ type: "horizontalRule" });
        break;
      case "image": {
        const src = n.attrs?.src;
        if (typeof src === "string" && IMAGE_SRC_PATTERN.test(src)) out.push({ type: "image", attrs: { src, alt: typeof n.attrs?.alt === "string" ? n.attrs.alt.slice(0, 200) : "" } });
        break;
      }
    }
  }
  return out.slice(0, max);
}

/** Tiptap JSON (or anything) → a PostDoc that postDocSchema accepts: unknown nodes, marks and attrs dropped. */
export function normalizePostDoc(json: unknown): PostDoc {
  const content = normBlocks((json as Json | null)?.content, 300);
  // Trailing empty paragraphs (Tiptap always keeps one) are not content.
  while (content.length && content.at(-1)!.type === "paragraph" && !(content.at(-1) as { content?: unknown[] }).content?.length) content.pop();
  return { type: "doc", content };
}

/** Plain text (blank line = paragraph, newline = line break) as a PostDoc: the start for rich editing. */
export function postDocFromText(text: string | undefined): PostDoc {
  const paras = (text ?? "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  return {
    type: "doc",
    content: paras.map((p) => ({
      type: "paragraph" as const,
      content: p.split("\n").flatMap((line, i): PostInline[] => (i ? [{ type: "hardBreak" }, ...(line ? [{ type: "text" as const, text: line }] : [])] : line ? [{ type: "text", text: line }] : [])),
    })),
  };
}

/** True when a doc has something to show. */
export const postDocHasContent = (doc: PostDoc | undefined) => !!doc?.content?.length;
